import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

import StarBalanceWidget from './components/StarBalanceWidget';
import RewardCard from './components/RewardCard';
import RedemptionModal from './components/RedemptionModal';
import RedemptionHistory from './components/RedemptionHistory';
import CelebrationOverlay from './components/CelebrationOverlay';
import {
  getStudentRewards,
  getAvailableRewards,
  getStudentRedemptions,
  redeemReward
} from '../../services/rewardService';

const CATEGORIES = [
  { id: 'all', label: 'All Rewards', icon: 'Grid3X3' },
  { id: 'academic', label: 'Academic', icon: 'BookOpen' },
  { id: 'privilege', label: 'Privileges', icon: 'Crown' },
  { id: 'experience', label: 'Experiences', icon: 'Sparkles' },
  { id: 'general', label: 'General', icon: 'Gift' },
];

const StudentRewardsCenter = () => {
  const navigate = useNavigate();
  const [studentSession, setStudentSession] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [balance, setBalance] = useState({ totalStars: 0, starsSpent: 0, starsAvailable: 0 });
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReward, setSelectedReward] = useState(null);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationStars, setCelebrationStars] = useState(0);

  const fetchData = useCallback(async (session) => {
    setLoading(true);
    try {
      const teacherId = session?.teacherId;

      const [rewardsRes, balanceRes, redemptionsRes] = await Promise.all([
        getAvailableRewards(teacherId),
        getStudentRewards(session?.id),
        getStudentRedemptions(session?.id)
      ]);

      setRewards(rewardsRes?.rewards || []);
      setBalance(balanceRes?.rewards || { totalStars: 0, starsSpent: 0, starsAvailable: 0 });
      setRedemptions(redemptionsRes?.redemptions || []);
    } catch (err) {
      console.error('Error fetching rewards data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const session = localStorage.getItem('studentSession');
    if (!session) {
      navigate('/student-login');
      return;
    }
    try {
      const parsed = JSON.parse(session);
      setStudentSession(parsed);
      fetchData(parsed);
    } catch {
      navigate('/student-login');
    }
  }, [navigate, fetchData]);

  const handleRedeemClick = (reward) => {
    setSelectedReward(reward);
    setShowRedemptionModal(true);
  };

  const handleConfirmRedeem = async () => {
    if (!selectedReward || !studentSession) return;

    const { redemption, error } = await redeemReward(
      studentSession?.id,
      selectedReward?.id,
      selectedReward?.starCost
    );

    if (error) {
      alert(error);
      return;
    }

    setShowRedemptionModal(false);
    setCelebrationStars(selectedReward?.starCost);
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 3000);

    // Refresh data
    fetchData(studentSession);
  };

  const filteredRewards = rewards?.filter(reward => {
    const matchesCategory = selectedCategory === 'all' || reward?.category === selectedCategory;
    const matchesSearch = !searchQuery ||
      reward?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      reward?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
        <div className="text-center">
          <div className="text-5xl mb-4 animate-bounce">⭐</div>
          <p className="text-amber-700 font-medium">Loading your rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Rewards Center - LearningTrails</title>
        <meta name="description" content="Redeem your earned stars for exciting rewards" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/student-dashboard')}
                  className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="ArrowLeft" size={20} />
                  <span className="text-sm font-medium hidden sm:inline">Dashboard</span>
                </button>
                <div className="w-px h-6 bg-border" />
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                    <span className="text-white text-sm">⭐</span>
                  </div>
                  <span className="text-lg font-bold text-foreground">Rewards Center</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors text-sm font-medium"
                >
                  <Icon name="History" size={16} />
                  <span className="hidden sm:inline">My Redemptions</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="pt-20 pb-12 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Star Balance Widget */}
            <StarBalanceWidget balance={balance} />

            {/* Redemption History (collapsible) */}
            {showHistory && (
              <RedemptionHistory
                redemptions={redemptions}
                onClose={() => setShowHistory(false)}
              />
            )}

            {/* Search & Filter */}
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search rewards..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e?.target?.value)}
                    className="w-full pl-9 pr-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {CATEGORIES?.map(cat => (
                    <button
                      key={cat?.id}
                      onClick={() => setSelectedCategory(cat?.id)}
                      className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedCategory === cat?.id
                          ? 'bg-amber-500 text-white' :'bg-amber-50 text-amber-700 hover:bg-amber-100'
                      }`}
                    >
                      <Icon name={cat?.icon} size={14} />
                      <span className="hidden md:inline">{cat?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Rewards Grid */}
            {filteredRewards?.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <div className="text-5xl mb-4">🎁</div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {rewards?.length === 0 ? 'No rewards available yet' : 'No rewards match your search'}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {rewards?.length === 0
                    ? 'Your teacher will add rewards soon. Keep earning stars!' :'Try a different search or category filter.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredRewards?.map(reward => (
                  <RewardCard
                    key={reward?.id}
                    reward={reward}
                    availableStars={balance?.starsAvailable || 0}
                    onRedeem={handleRedeemClick}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Redemption Confirmation Modal */}
        {showRedemptionModal && selectedReward && (
          <RedemptionModal
            reward={selectedReward}
            availableStars={balance?.starsAvailable || 0}
            onConfirm={handleConfirmRedeem}
            onCancel={() => setShowRedemptionModal(false)}
          />
        )}

        {/* Celebration Overlay */}
        {showCelebration && (
          <CelebrationOverlay stars={celebrationStars} />
        )}
      </div>
    </>
  );
};

export default StudentRewardsCenter;
